/**
 * Transition spiral reconstruction.
 *
 * LandXML never stores spiral geometry - only endpoints, the two radii, length,
 * rotation and `spiType` (plus optional theta/totalX/totalY/tanLong/tanShort).
 * The shape has to be integrated. `INF` is a legal radius, so a "simple" spiral
 * has exactly one infinite radius and a compound spiral has neither.
 *
 * Shape functions map normalised arc length `u` in [0,1] to the fraction of the
 * curvature change that has been applied. Clothoid is linear; the rest are the
 * standard railway forms. Unknown types fall back to clothoid *and say so*.
 */

import type { SpiralEl } from '../landxml/types';
import { azimuth, normAz } from './arc';

export type ShapeFn = (u: number) => number;

const SHAPES: Record<string, ShapeFn> = {
  clothoid: (u) => u,
  cubicparabola: (u) => u,
  cubic: (u) => u,
  japanesecubic: (u) => u,
  bloss: (u) => 3 * u * u - 2 * u * u * u,
  cosine: (u) => (1 - Math.cos(Math.PI * u)) / 2,
  sinusoid: (u) => u - Math.sin(2 * Math.PI * u) / (2 * Math.PI),
  biquadratic: (u) => (u <= 0.5 ? 2 * u * u : 1 - 2 * (1 - u) * (1 - u)),
  biquadraticparabola: (u) => (u <= 0.5 ? 2 * u * u : 1 - 2 * (1 - u) * (1 - u)),
  sinehalfwave: (u) => Math.sin((Math.PI * u) / 2),
  radioid: (u) => u,
  weinerbogen: (u) => 3 * u * u - 2 * u * u * u,
};

function shapeFor(spiType: string): { f: ShapeFn; substituted: boolean } {
  const key = spiType.trim().toLowerCase().replace(/[^a-z]/g, '');
  const rev = key.startsWith('rev');
  const base = rev ? key.slice(3) : key;
  const f = SHAPES[base];
  if (!f) return { f: SHAPES.clothoid, substituted: true };
  return { f: rev ? (u: number) => 1 - f(1 - u) : f, substituted: false };
}

export interface SpiralLocal {
  /** Local frame coordinates, x along the entry tangent, y to the left. */
  x: Float64Array;
  y: Float64Array;
  /** Deflection from the entry tangent at each sample, radians. */
  theta: Float64Array;
  /** Arc length at each sample. */
  s: Float64Array;
  substituted: boolean;
}

/**
 * Integrate the spiral in a local frame using composite Simpson on cos/sin of
 * the running deflection. `n` is forced even and chosen so the deflection step
 * stays small; Simpson then lands within ~1e-10 of the exact integral over the
 * angles civil and rail geometry actually uses.
 */
export function integrateSpiral(
  L: number, kStart: number, kEnd: number, spiType: string, samples?: number,
): SpiralLocal {
  const { f, substituted } = shapeFor(spiType);
  // Panel count from the total deflection: Simpson's error goes as h^4, and
  // ~0.002 rad per panel puts the integrated end point inside a few nanometres
  // of the converged value for every civil and rail spiral.
  const dTheta = Math.abs((kStart + kEnd) / 2) * L;
  let n = samples ?? Math.max(32, Math.ceil(dTheta / 0.002) * 2);
  if (n % 2) n++;
  n = Math.min(4096, Math.max(4, n));

  const h = L / n;
  const s = new Float64Array(n + 1);
  const th = new Float64Array(n + 1);
  const x = new Float64Array(n + 1);
  const y = new Float64Array(n + 1);

  // theta(s) = integral of curvature. Simpson on kappa as well, so non-linear
  // shape functions integrate to the same order as the position integral.
  const kap = (i: number) => {
    const u = (i * h) / L;
    return kStart + (kEnd - kStart) * f(u);
  };
  for (let i = 0; i <= n; i++) s[i] = i * h;
  th[0] = 0;
  for (let i = 1; i <= n; i++) {
    // trapezoid + midpoint (Simpson on the half interval)
    const km = kStart + (kEnd - kStart) * f(((i - 0.5) * h) / L);
    th[i] = th[i - 1] + (h / 6) * (kap(i - 1) + 4 * km + kap(i));
  }

  x[0] = 0; y[0] = 0;
  for (let i = 2; i <= n; i += 2) {
    const c0 = Math.cos(th[i - 2]), c1 = Math.cos(th[i - 1]), c2 = Math.cos(th[i]);
    const s0 = Math.sin(th[i - 2]), s1 = Math.sin(th[i - 1]), s2 = Math.sin(th[i]);
    x[i] = x[i - 2] + (h / 3) * (c0 + 4 * c1 + c2);
    y[i] = y[i - 2] + (h / 3) * (s0 + 4 * s1 + s2);
    // Odd sample: Simpson over the first half of the panel.
    const cm = Math.cos((th[i - 2] + th[i - 1]) / 2);
    const sm = Math.sin((th[i - 2] + th[i - 1]) / 2);
    x[i - 1] = x[i - 2] + (h / 6) * (c0 + 4 * cm + c1);
    y[i - 1] = y[i - 2] + (h / 6) * (s0 + 4 * sm + s1);
  }
  return { x, y, theta: th, s, substituted };
}

export interface SpiralSolution {
  /** World-space samples, (east, north). */
  pts: Array<[number, number]>;
  /** Arc length at each sample. */
  sta: Float64Array;
  length: number;
  azStart: number;
  azEnd: number;
  substituted: boolean;
  /** Distance between the integrated end point and the file's `End`. */
  closureError?: number;
  note?: string;
}

/**
 * Place a spiral in the world.
 *
 * Anchoring: we run from whichever end has the larger radius (the flatter end),
 * because that is where the tangent direction is well defined - from `Start`
 * towards `PI`/`End` when `radiusStart >= radiusEnd`, otherwise backwards from
 * `End`. The entry azimuth is taken from the chord to `PI` when a `PI` exists
 * (exact for a spiral, whose long/short tangents meet there), else from the
 * declared `dirStart`/`dirEnd`, else from the `Start -> End` chord rotated by
 * the analytic chord deflection.
 */
export function solveSpiral(sp: SpiralEl, warn?: (m: string) => void): SpiralSolution | null {
  const S = sp.start, E = sp.end;
  const haveS = S?.x !== undefined && S?.y !== undefined;
  const haveE = E?.x !== undefined && E?.y !== undefined;
  const L = sp.length;
  if (!(L > 0) || (!haveS && !haveE)) return null;

  const k1 = Number.isFinite(sp.radiusStart) && sp.radiusStart !== 0 ? 1 / sp.radiusStart : 0;
  const k2 = Number.isFinite(sp.radiusEnd) && sp.radiusEnd !== 0 ? 1 / sp.radiusEnd : 0;
  const side = sp.rot === 'ccw' ? 1 : -1; // +1: centre of curvature to the left

  // Run forward from Start unless only End is known.
  const forward = haveS;
  const local = integrateSpiral(L, forward ? Math.abs(k1) : Math.abs(k2), forward ? Math.abs(k2) : Math.abs(k1), sp.spiType);

  const anchor = forward ? S! : E!;
  const other = forward ? E : S;

  // Entry azimuth.
  let az0: number | undefined;
  const pi = sp.pi;
  if (pi?.x !== undefined && pi?.y !== undefined) {
    const dx = pi.x - anchor.x!, dy = pi.y - anchor.y!;
    if (Math.hypot(dx, dy) > 1e-9) az0 = azimuth(dx, dy);
  }
  if (az0 === undefined) {
    const declared = forward ? sp.dirStart : sp.dirEnd;
    if (declared !== undefined) az0 = forward ? declared : declared + Math.PI;
  }
  if (az0 === undefined && other?.x !== undefined && other?.y !== undefined) {
    // Rotate the chord back by the chord's own deflection from the entry tangent.
    const dx = other.x - anchor.x!, dy = other.y - anchor.y!;
    const chordAz = azimuth(dx, dy);
    const nn = local.x.length - 1;
    // The chord leaves the entry tangent by this much, towards the centre side.
    const chordDefl = Math.atan2(local.y[nn], local.x[nn]);
    az0 = chordAz + side * (forward ? 1 : -1) * chordDefl;
  }
  if (az0 === undefined) return null;

  // Walking frame: t = (sin az, cos az) in (east, north); the local +y axis points
  // at the centre of curvature, which is to the LEFT of the walk when dirSide = +1.
  const sa = Math.sin(az0), ca = Math.cos(az0);
  const dirSide = side * (forward ? 1 : -1);
  const nx = -ca * dirSide, ny = sa * dirSide; // signed normal, (east, north)
  const n = local.x.length - 1;
  const pts: Array<[number, number]> = new Array(n + 1);
  for (let i = 0; i <= n; i++) {
    const lx = local.x[i], ly = local.y[i];
    pts[i] = [anchor.x! + lx * sa + ly * nx, anchor.y! + lx * ca + ly * ny];
  }
  const sta = local.s.slice();
  if (!forward) {
    pts.reverse();
    for (let i = 0; i <= n; i++) sta[i] = L - local.s[n - i];
  }

  let closureError: number | undefined;
  if (haveS && haveE) {
    const last = pts[pts.length - 1];
    const target = forward ? E! : S!;
    closureError = Math.hypot(last[0] - target.x!, last[1] - target.y!);
    const tol = Math.max(1e-3, 1e-5 * L);
    if (closureError > tol && warn) {
      warn(`Spiral "${sp.name ?? ''}": integrated end point is ${closureError.toFixed(4)} from the declared End`);
    }
  }

  // Cross-check against the declared analytic quantities when they exist.
  if (warn) {
    if (sp.totalX !== undefined && Math.abs(local.x[n] - sp.totalX) > 1e-3 * Math.max(1, L)) {
      warn(`Spiral "${sp.name ?? ''}": totalX ${sp.totalX} vs integrated ${local.x[n].toFixed(4)}`);
    }
    if (sp.totalY !== undefined && Math.abs(local.y[n] - sp.totalY) > 1e-3 * Math.max(1, L)) {
      warn(`Spiral "${sp.name ?? ''}": totalY ${sp.totalY} vs integrated ${local.y[n].toFixed(4)}`);
    }
    if (local.substituted) {
      warn(`Spiral "${sp.name ?? ''}": spiType "${sp.spiType}" is not implemented; approximated as a clothoid`);
    }
  }

  // Walking azimuth turns towards the centre: clockwise when the centre is on the
  // right (dirSide = -1), counter-clockwise when it is on the left.
  const azWalkEnd = az0 - dirSide * local.theta[n];
  const azStart = forward ? az0 : azWalkEnd + Math.PI;
  const azEnd = forward ? azWalkEnd : az0 + Math.PI;
  return {
    pts, sta, length: L,
    azStart: normAz(azStart),
    azEnd: normAz(azEnd),
    substituted: local.substituted,
    closureError,
  };
}

/** Clothoid parameter A, where A^2 = L * R. */
export function clothoidA(L: number, R: number): number {
  return Math.sqrt(Math.abs(L * R));
}
